<template>
  <PayOpsModalShell
    :visible="visible"
    :title="incident ? `Incident Orders: ${incident.name}` : 'Incident Orders'"
    subtitle="View assigned order cases and trigger one-click batch correction"
    icon="fa-list-alt"
    size="xl"
    @close="$emit('close')"
  >
    <div v-if="incident" class="incident-meta border rounded-3 p-3 mb-3">
      <div class="row g-2 small">
        <div class="col-md-6">
          <strong>Description:</strong> {{ incident.description || '—' }}
        </div>
        <div class="col-md-6">
          <strong>Default Reason:</strong> {{ incident.defaultReason || '—' }}
        </div>
      </div>
    </div>

    <!-- Member UUIDs with no amendment case: they would be silently skipped by a
         batch correction, so name them rather than just showing a smaller count. -->
    <div
      v-if="!isLoading && missingOrderUuids.length > 0"
      class="alert alert-warning border d-flex align-items-start gap-2 mb-3"
    >
      <i class="fa fa-unlink text-warning mt-1"></i>
      <div>
        <strong class="d-block mb-1">
          {{ missingOrderUuids.length }} attached order(s) have no amendment case
        </strong>
        <span class="small">
          They are members of this incident but no case document exists, so a batch
          correction will not touch them:
          <code class="d-block mt-1">{{ missingOrderUuids.join(', ') }}</code>
        </span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-center py-4">
      <div class="spinner-border text-primary"></div>
      <p class="text-muted mt-2">Loading incident orders...</p>
    </div>

    <!-- Error -->
    <div v-else-if="loadError" class="alert alert-danger outline d-flex align-items-center mb-3">
      <span class="flex-grow-1">{{ loadError }}</span>
      <button class="btn btn-sm btn-outline-danger ms-2" @click="fetchCases">
        <i class="fa fa-refresh me-1"></i>Retry
      </button>
    </div>

    <!-- Empty -->
    <div v-else-if="cases.length === 0" class="oa-empty-state py-4 text-center">
      <i class="fa fa-info-circle fa-2x text-muted mb-2"></i>
      <p class="text-muted mb-0">No orders attached to this incident yet.</p>
    </div>

    <!-- Table -->
    <div v-else class="incident-orders-table">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span class="small fw-bold text-muted">{{ cases.length }} order(s) attached</span>
        <button
          v-if="hasAgentAccess"
          class="btn btn-sm btn-success"
          @click="confirmModalOpen = true"
        >
          <i class="fa fa-check-circle me-1"></i>Correct / Complete Incident Orders
        </button>
      </div>

      <div class="table-responsive border rounded-3">
        <table class="table table-sm table-striped mb-0">
          <thead class="table-light">
            <tr>
              <th>#</th>
              <th>Order UUID</th>
              <th>Order State</th>
              <th>Classification</th>
              <th>Partner</th>
              <th>Amount</th>
              <th>Assignee</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(c, idx) in cases" :key="c.orderUuid">
              <td class="small text-muted">{{ idx + 1 }}</td>
              <td class="font-monospace small">
                <router-link
                  :to="{
                    name: 'pay-operations-order-amendment-detail',
                    params: { uuid: c.orderUuid },
                  }"
                  target="_blank"
                >
                  {{ c.orderUuid }}
                </router-link>
              </td>
              <td>
                <span class="badge" :class="stateBadgeClass(c.orderState)">
                  {{ c.orderState || 'UNKNOWN' }}
                </span>
              </td>
              <td>
                <span class="badge text-bg-dark">{{ c.classification || 'standard' }}</span>
              </td>
              <td class="small">{{ c.partnerName || '—' }}</td>
              <td class="small">{{ formatCurrency(c.totalAmount, c.currency) }}</td>
              <td class="small">{{ c.assigneeDisplayName || c.assignee || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <template #footer>
      <button class="btn btn-outline-secondary" @click="$emit('close')">Close</button>
    </template>
  </PayOpsModalShell>

  <BulkActionConfirmModal
    :visible="confirmModalOpen"
    action="completeManually"
    :orders="cases"
    :default-reason="incident?.defaultReason || ''"
    :is-submitting="isCreatingBulkJob"
    @close="confirmModalOpen = false"
    @submit="executeBatchCorrection"
  />

  <BulkProgressModal
    :visible="bulkProgressOpen"
    :job-id="bulkJobId"
    :can-retry="hasAdminAccess"
    @close="bulkProgressOpen = false"
  />
</template>

<script setup>
import { ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import PayOpsModalShell from '../../shared/PayOpsModalShell.vue';
import BulkActionConfirmModal from '../../bulk/BulkActionConfirmModal.vue';
import BulkProgressModal from '../../bulk/BulkProgressModal.vue';
import { formatCurrency } from '@/utils/display-formatters';
import { stateBadgeClass } from '@/services/ryd-pay-operations/order-amendments/formatters';
import { useAmendmentRoles } from '@/services/ryd-pay-operations/order-amendments/use-amendment-roles';

const props = defineProps({
  visible: { type: Boolean, default: false },
  incident: { type: Object, default: null },
});

const emit = defineEmits(['close']);

const toast = useToast();
const { hasAgentAccess, hasAdminAccess } = useAmendmentRoles();

const cases = ref([]);
const missingOrderUuids = ref([]);
const isLoading = ref(false);
const loadError = ref('');

const confirmModalOpen = ref(false);
const isCreatingBulkJob = ref(false);
const bulkJobId = ref('');
const bulkProgressOpen = ref(false);

async function fetchCases() {
  if (!props.incident?.id) return;
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.payOperations.orderAmendments.incidentCases(props.incident.id),
    );
    cases.value = data?.items || [];
    missingOrderUuids.value = data?.missingOrderUuids || [];
  } catch (err) {
    loadError.value = err?.response?.data?.message || 'Failed to load incident orders.';
  } finally {
    isLoading.value = false;
  }
}

async function executeBatchCorrection({
  action,
  reason,
  captureOnAmend,
  orderUuids,
  confirmations,
}) {
  if (!orderUuids?.length || reason.length < 10) return;
  isCreatingBulkJob.value = true;
  try {
    const { data } = await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.bulkJobs,
      {
        action,
        type: `incident-${props.incident?.id || 'batch'}`,
        reason,
        captureOnAmend,
        orderUuids,
        confirmations,
      },
    );
    bulkJobId.value = data.jobId;
    confirmModalOpen.value = false;
    bulkProgressOpen.value = true;
    toast.success('Incident batch correction job created.');
    await fetchCases();
  } catch (err) {
    toast.error(err?.response?.data?.message || 'Failed to create bulk job.');
  } finally {
    isCreatingBulkJob.value = false;
  }
}

watch(
  () => [props.visible, props.incident?.id],
  ([visible]) => {
    if (visible && props.incident?.id) {
      fetchCases();
    } else {
      cases.value = [];
      missingOrderUuids.value = [];
    }
  },
);
</script>

<style scoped>
/* Theme var so the panel follows light/dark; spacing and border come from Bootstrap. */
.incident-meta {
  background: var(--light-background);
}
</style>
